import React, { useState } from "react";
import { IoMailOutline } from "react-icons/io5";
import {
  FiEye,
  FiMinus,
  FiMaximize2,
  FiMinimize2,
  FiX,
  FiSend,
} from "react-icons/fi";
import ShadowCard from "../../../components/common/ShadowCard";
import IconComponent from "../../../components/common/IconComponent";
import { FormRenderProps } from "@progress/kendo-react-form";

const MailIcon = IoMailOutline as any;
const EyeIcon = FiEye as any;
const MinusIcon = FiMinus as any;
const MaximizeIcon = FiMaximize2 as any;
const MinimizeIcon = FiMinimize2 as any;
const CloseIcon = FiX as any;
const SendIcon = FiSend as any;

interface EmailLivePreviewProps {
  selectedTemplate: any;
  templateLoading: boolean;
  customSubject: string;
  selectedCount: number;
  firstSelectedContact: any;
  activeVariables: number[];
  formRenderProps: FormRenderProps;
  renderPreviewWithVariables: (
    text: string,
    formRenderProps: FormRenderProps,
  ) => string;
  onSendClick?: () => void;
  sending?: boolean;
  isSendAccess?: boolean;
  isFloating?: boolean;
}

export const EmailLivePreview: React.FC<EmailLivePreviewProps> = ({
  selectedTemplate,
  templateLoading,
  customSubject,
  selectedCount,
  firstSelectedContact,
  formRenderProps,
  renderPreviewWithVariables,
  onSendClick,
  sending = false,
  isSendAccess = true,
  isFloating = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [isMaximized, setIsMaximized] = useState<boolean>(false);

  const previewSubject =
    renderPreviewWithVariables(
      customSubject || selectedTemplate?.Subject || "",
      formRenderProps,
    ) || "(No Subject)";

  const previewBody = selectedTemplate?.Content
    ? renderPreviewWithVariables(selectedTemplate.Content, formRenderProps)
    : "";

  const renderContent = () => (
    <>
      {/* Envelope Details */}
      <div
        style={{
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          padding: "12px 18px",
          fontSize: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          color: "#334155",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <strong style={{ color: "#64748b", width: "60px" }}>From:</strong>
          <span style={{ fontWeight: 600, color: "#1e293b" }}>
            Kavish Hotels &lt;directorsales@kavishhotels.com&gt;
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <strong style={{ color: "#64748b", width: "60px" }}>To:</strong>
          <span style={{ color: "#2563eb", fontWeight: 500 }}>
            {selectedCount > 0
              ? firstSelectedContact
                ? `${firstSelectedContact.FirstName || ""} ${
                    firstSelectedContact.LastName || ""
                  } <${firstSelectedContact.EmailID}>${
                    selectedCount > 1 ? ` (+${selectedCount - 1} others)` : ""
                  }`
                : `${selectedCount} Selected contacts`
              : "(Select contacts from list)"}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <strong style={{ color: "#64748b", width: "60px" }}>Subject:</strong>
          <span
            style={{
              fontWeight: 700,
              color: "#0f172a",
              backgroundColor: "#ffffff",
              padding: "3px 8px",
              borderRadius: "4px",
              border: "1px solid #cbd5e1",
              wordBreak: "break-word",
            }}
          >
            {previewSubject}
          </span>
        </div>
      </div>

      {/* HTML Email Body Container */}
      <div
        style={{
          padding: "20px",
          minHeight: isMaximized ? "calc(80vh - 180px)" : "260px",
          maxHeight: isMaximized ? "calc(85vh - 160px)" : "420px",
          overflowY: "auto",
          fontSize: "13px",
          color: "#1e293b",
          lineHeight: "1.6",
          backgroundColor: "#ffffff",
        }}
      >
        {templateLoading ? (
          <div
            style={{
              textAlign: "center",
              padding: "50px 0",
              color: "#64748b",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div className="k-i-loading k-icon" style={{ fontSize: "24px" }} />
            <span>Loading email preview...</span>
          </div>
        ) : selectedTemplate?.Content ? (
          <div
            style={{
              width: "100%",
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
            dangerouslySetInnerHTML={{ __html: previewBody }}
          />
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#94a3b8",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#cbd5e1",
              }}
            >
              <IconComponent icon={MailIcon} style={{ fontSize: "28px" }} />
            </div>
            <div>
              <div
                style={{ fontWeight: 600, color: "#64748b", fontSize: "14px" }}
              >
                No Email Template Selected
              </div>
              <div
                style={{ fontSize: "12px", marginTop: "4px", color: "#94a3b8" }}
              >
                Select an email template to render live email preview
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Footer Bar for Floating Compose Mode */}
      {isFloating && onSendClick && (
        <div
          style={{
            padding: "12px 18px",
            backgroundColor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: "12px", color: "#64748b" }}>
            {selectedCount > 0
              ? `${selectedCount} contacts ready`
              : "No contacts selected"}
          </div>

          <button
            type="button"
            onClick={onSendClick}
            disabled={
              sending ||
              !selectedTemplate ||
              selectedCount === 0 ||
              !isSendAccess
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 18px",
              borderRadius: "8px",
              backgroundColor:
                sending ||
                !selectedTemplate ||
                selectedCount === 0 ||
                !isSendAccess
                  ? "#cbd5e1"
                  : "#2563eb",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "13px",
              border: "none",
              cursor:
                sending ||
                !selectedTemplate ||
                selectedCount === 0 ||
                !isSendAccess
                  ? "not-allowed"
                  : "pointer",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)",
              transition: "all 0.2s ease",
            }}
          >
            <IconComponent icon={SendIcon} style={{ fontSize: "14px" }} />
            <span>{sending ? "Sending..." : "Send Bulk Email"}</span>
          </button>
        </div>
      )}
    </>
  );

  // FLOATING GMAIL-STYLE COMPOSE WINDOW MODE
  if (isFloating) {
    if (!isOpen) {
      // Floating Bottom-Right Trigger Button
      return (
        <button
          type="button"
          onClick={() => {
            setIsOpen(true);
            setIsMinimized(false);
          }}
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 20px",
            borderRadius: "30px",
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            color: "#ffffff",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.4)",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: "13px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconComponent
              icon={MailIcon}
              style={{ fontSize: "20px", color: "#38bdf8" }}
            />
            {selectedTemplate && (
              <span
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#34d399",
                  boxShadow: "0 0 8px #34d399",
                }}
              />
            )}
          </div>
          <span>Email Preview</span>
          {selectedCount > 0 && (
            <span
              style={{
                backgroundColor: "#2563eb",
                color: "#ffffff",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "11px",
                fontWeight: 700,
              }}
            >
              {selectedCount}
            </span>
          )}
        </button>
      );
    }

    // Gmail-style Floating Window (Bottom-Right)
    return (
      <div
        style={{
          position: "fixed",
          bottom: isMinimized ? "0px" : isMaximized ? "50%" : "0px",
          right: isMinimized ? "24px" : isMaximized ? "50%" : "24px",
          transform: !isMinimized && isMaximized ? "translate(50%, 50%)" : "none",
          width: isMinimized ? "400px" : isMaximized ? "850px" : "500px",
          maxWidth: "92vw",
          height: isMinimized ? "44px" : "auto",
          maxHeight: isMinimized ? "44px" : isMaximized ? "90vh" : "600px",
          zIndex: 9999,
          borderRadius: isMinimized ? "10px 10px 0 0" : isMaximized ? "16px" : "12px 12px 0 0",
          backgroundColor: "#ffffff",
          boxShadow: "0 20px 50px -10px rgba(15, 23, 42, 0.4)",
          border: "1px solid #cbd5e1",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.25s ease-out",
        }}
      >
        {/* Gmail Compose Top Window Bar */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#ffffff",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            userSelect: "none",
            cursor: "pointer",
          }}
          onClick={() => setIsMinimized(!isMinimized)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconComponent
              icon={MailIcon}
              style={{ fontSize: "17px", color: "#38bdf8" }}
            />
            <span
              style={{ fontWeight: 700, fontSize: "13px", color: "#ffffff" }}
            >
              {selectedTemplate
                ? `Preview: ${selectedTemplate.Subject || "Email Template"}`
                : "New Message Preview"}
            </span>
          </div>

          <div
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Minimize */}
            <button
              type="button"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? "Expand" : "Minimize"}
              style={{
                background: "transparent",
                border: "none",
                color: "#cbd5e1",
                cursor: "pointer",
                padding: "2px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconComponent icon={MinusIcon} style={{ fontSize: "16px" }} />
            </button>

            {/* Maximize Toggle */}
            <button
              type="button"
              onClick={() => {
                setIsMaximized(!isMaximized);
                setIsMinimized(false);
              }}
              title={isMaximized ? "Restore Size" : "Full Screen"}
              style={{
                background: "transparent",
                border: "none",
                color: "#cbd5e1",
                cursor: "pointer",
                padding: "2px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconComponent
                icon={isMaximized ? MinimizeIcon : MaximizeIcon}
                style={{ fontSize: "14px" }}
              />
            </button>

            {/* Close */}
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              title="Close Preview"
              style={{
                background: "transparent",
                border: "none",
                color: "#cbd5e1",
                cursor: "pointer",
                padding: "2px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconComponent icon={CloseIcon} style={{ fontSize: "17px" }} />
            </button>
          </div>
        </div>

        {/* Content (only shown when not minimized) */}
        {!isMinimized && renderContent()}
      </div>
    );
  }

  // INLINE CARD MODE (Standard embedded preview card)
  return (
    <ShadowCard
      style={{
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
        backgroundColor: "#ffffff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header Bar */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#ffffff",
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #334155",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconComponent
              icon={MailIcon}
              style={{ fontSize: "18px", color: "#ffffff" }}
            />
          </div>
          <div>
            <div
              style={{ fontWeight: 700, fontSize: "14px", color: "#ffffff" }}
            >
              Live Email Preview
            </div>
            <div style={{ fontSize: "11px", color: "#94a3b8" }}>
              {selectedTemplate
                ? selectedTemplate.Subject
                : "No template selected"}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: selectedTemplate
              ? "rgba(16, 185, 129, 0.2)"
              : "rgba(148, 163, 184, 0.2)",
            color: selectedTemplate ? "#34d399" : "#cbd5e1",
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: 600,
          }}
        >
          <IconComponent icon={EyeIcon} style={{ fontSize: "12px" }} />
          <span>{selectedTemplate ? "Live Interactive" : "Idle"}</span>
        </div>
      </div>

      {renderContent()}
    </ShadowCard>
  );
};
